import React, { useState, useEffect, useRef } from 'react';
import Header from '../../components/Header';
import BasicCard from '../../components/BasicCard';
import clsx from 'clsx';
import CustomButton from '../../components/CustomButton';
import { headerStyle, inputStyle, noteStyle } from '../../constants/styles';
import InputCardItems from '../../components/InputCardItems';
import Loader from '../../global/loader';
import apiRequest from '../../utils/apiRequest';

import CustomLine from '../../components/CustomLine';
import toast from 'react-hot-toast';
import { useLocation, useNavigate } from 'react-router-dom';

import ReactQuill from 'react-quill';
import 'react-quill/dist/quill.snow.css';
import { modules } from '../../constants/toolBarModules';
import { MdDelete } from 'react-icons/md';
import { Dialog, DialogTitle, List } from '@mui/material';

type BenefitType = {
  benefitId?: string;
  id: number;
  benefitIcon: File | string | null;
  benefitTitle: string;
};

type TierDataType = {
  id?: string;
  rangeFrom: string;
  rangeTo: string;
  storeLogo: File | string | null;
  tierIcon: File | string | null;
  backgroundColor: string;
  tierName: string;
  title: string;
  description: string;
  backgroundColor1: string;
  backgroundColor2: string;
  programHeading: string;
  programSubDescription: string;
  milestoneHeading: string;
  milestoneDescription: string;
  milestoneProgress: string;
  milestoneContent: string;
  isActive: string;
  ctaButtonName: string;
  ctaButtonLink: string;
  ctaDescription: string;
  benefitTitle: string;
};

type TierResponseType = {
  tierId: number;
  rangeFrom: string;
  rangeTo: string;
  tierName: string;
  backgroundColor: string;
  storeLogo: string;
  tierIcon: string;
  programHeading: string;
  programSubDescription: string;
  claimedIcon: string | null;
  pointsIcon: string | null;
  pointsEarned: number | null;
  status: boolean;
  milestoneHeading: string;
  milestoneDescription: string;
  milestoneProgress: string;
  milestoneContent: string;
  ctaButtonName: string;
  ctaButtonLink: string;
  ctaDescription: string;
  howItWorks: string | null;
  benefits: BenefitResponseType[];
  benefitTitle: string;
  benefitDescription: string;
  benefitBackgroundColor1: string;
  benefitBackgroundColor2: string;
};

type BenefitResponseType = {
  id: number;
  title: string;
  benefitIcon: string;
};

const TierForm = () => {
  const [loader, setLoader] = useState(false);
  const [isPopupOpen, setIsPopupOpen] = useState(false);
  const [popupIndex, setPopupIndex] = useState<number | null>(null);

  const [tierData, setTierData] = useState<TierDataType>({
    benefitTitle: '',
    tierName: '',
    rangeFrom: '',
    rangeTo: '',
    storeLogo: null,
    tierIcon: null,
    backgroundColor: '#ffffff',
    programHeading: '',
    programSubDescription: '',
    milestoneHeading: '',
    milestoneDescription: '',
    milestoneProgress: '',
    milestoneContent: '',
    isActive: 'true',
    ctaButtonName: '',
    ctaButtonLink: '',
    ctaDescription: '',
    title: '',
    description: '',
    backgroundColor1: '',
    backgroundColor2: '',
  });
  const [benefitData, setBenefitData] = useState<BenefitType[]>([
    {
      id: 0,
      benefitIcon: null,
      benefitTitle: '',
    },
  ]);

  const [initialTierData, setInitialTierData] = useState<TierDataType | null>(
    null
  );
  const [initialBenefitData, setInitialBenefitData] = useState<BenefitType[]>(
    []
  );
  const [description, setDescription] = useState('');

  const storeLogoInputRef = useRef<HTMLInputElement | null>(null);
  const tierIconInputRef = useRef<HTMLInputElement | null>(null);
  const navigator = useNavigate();
  const location = useLocation();

  const queryParams = new URLSearchParams(location.search);
  const isEdit = queryParams.get('type') === 'edit';
  const tierId = queryParams.get('id');

  const handleDivClick = (ref: React.RefObject<HTMLInputElement>) => {
    if (ref.current) {
      ref.current.click();
    }
  };

  const handleFileChange = (
    e: React.ChangeEvent<HTMLInputElement>,
    fieldName: 'storeLogo' | 'tierIcon'
  ) => {
    const file = e.target.files?.[0] || null;
    setTierData((prev) => ({ ...prev, [fieldName]: file }));
  };

  const addMorePoints = () => {
    const newId = benefitData.length;

    setBenefitData((prevPoints) => [
      ...prevPoints,
      {
        id: newId,
        title: '',
        benefitIcon: null,
        benefitTitle: '',
      },
    ]);
  };

  const validateForm = () => {
    // Check tierData fields
    for (const key in tierData) {
      if (Object.hasOwnProperty.call(tierData, key)) {
        if (!tierData[key as keyof TierDataType]) {
          return false;
        }
      }
    }

    for (let i = 0; i < benefitData.length; i++) {
      const benefit = benefitData[i];
      for (const key in benefit) {
        if (Object.hasOwnProperty.call(benefit, key)) {
          if (!benefit[key as keyof BenefitType] && key !== 'id') {
            console.log('key is:', key);
            return false;
          }
        }
      }
    }
    return true;
  };

  const handleCreate = async () => {
    const formData = new FormData();

    formData.append('spendRangeFrom', tierData.rangeFrom);
    formData.append('spendRangeTo', tierData.rangeTo);
    if (tierData.storeLogo) formData.append('storeLogo', tierData.storeLogo);
    if (tierData.tierIcon) formData.append('tierIcon', tierData.tierIcon);
    formData.append('backgroundColor', tierData.backgroundColor);
    formData.append('tierName', tierData.tierName);
    formData.append('programHeading', tierData.programHeading);
    formData.append('programSubDescription', tierData.programSubDescription);
    formData.append('milestoneHeading', tierData.milestoneHeading);
    formData.append('milestoneDescription', tierData.milestoneDescription);
    formData.append('milestoneProgress', tierData.milestoneProgress);
    formData.append('milestoneContent', tierData.milestoneContent);
    formData.append('ctaButtonName', tierData.ctaButtonName);
    formData.append('ctaButtonLink', tierData.ctaButtonLink);
    formData.append('ctaButtonDescription', tierData.ctaDescription);
    formData.append('isActive', tierData.isActive);
    formData.append('benefitTitle', tierData.title);
    formData.append('benefitDescription', description);
    formData.append('backgroundColor1', tierData.backgroundColor1);
    formData.append('backgroundColor2', tierData.backgroundColor2);

    // Handle benefits data
    benefitData.forEach((benefit, index) => {
      if (benefit.benefitIcon) {
        formData.append(`points[${index}].benefitsIcon`, benefit.benefitIcon);
      }
      formData.append(`points[${index}].benefitsText`, benefit.benefitTitle);
    });

    formData.forEach((value, key) => {
      console.log(`${key}:`, value);
    });

    setLoader(true);

    try {
      const response = await apiRequest('createTier', formData);
      if (response.error) {
        toast.error(response.error?.error || 'An error occurred');
      } else {
        navigator('/tier');
        toast.success(
          isEdit ? 'Tier updated successfully' : 'Tier created successfully'
        );
      }
    } catch (error) {
      console.error('Error during submission:', error);
      toast.error('Failed to submit the form. Please try again.');
    } finally {
      setLoader(false);
    }
  };

  const handleUpdate = async () => {
    setLoader(true);
    const formData = new FormData();

    formData.append('tierId', tierData.id || '');
    formData.append('isActive', tierData.isActive);

    if (tierData.rangeFrom !== initialTierData?.rangeFrom) {
      formData.append('spendRangeFrom', tierData.rangeFrom);
    }
    if (tierData.rangeTo !== initialTierData?.rangeTo) {
      formData.append('spendRangeTo', tierData.rangeTo);
    }
    if (tierData.backgroundColor !== initialTierData?.backgroundColor) {
      formData.append('backgroundColor', tierData.backgroundColor);
    }
    if (tierData.tierName !== initialTierData?.tierName) {
      formData.append('tierName', tierData.tierName);
    }
    if (tierData.programHeading !== initialTierData?.programHeading) {
      formData.append('programHeading', tierData.programHeading);
    }
    if (
      tierData.programSubDescription !== initialTierData?.programSubDescription
    ) {
      formData.append('programSubDescription', tierData.programSubDescription);
    }
    if (tierData.milestoneHeading !== initialTierData?.milestoneHeading) {
      formData.append('milestoneHeading', tierData.milestoneHeading);
    }

    if (tierData.title !== initialTierData?.title) {
      formData.append('benefitTitle', tierData.title);
    }

    if (
      tierData.milestoneDescription !== initialTierData?.milestoneDescription
    ) {
      formData.append('milestoneDescription', tierData.milestoneDescription);
    }
    if (tierData.milestoneProgress !== initialTierData?.milestoneProgress) {
      formData.append('milestoneProgress', tierData.milestoneProgress);
    }
    if (tierData.milestoneContent !== initialTierData?.milestoneContent) {
      formData.append('milestoneContent', tierData.milestoneContent);
    }
    if (tierData.ctaButtonLink !== initialTierData?.ctaButtonLink) {
      formData.append('ctaButtonLink', tierData.ctaButtonLink);
    }
    if (tierData.ctaButtonName !== initialTierData?.ctaButtonName) {
      formData.append('ctaButtonName', tierData.ctaButtonName);
    }
    if (tierData.ctaDescription !== initialTierData?.ctaDescription) {
      formData.append('ctaButtonDescription', tierData.ctaDescription);
    }
    if (
      tierData.storeLogo &&
      tierData.storeLogo !== initialTierData?.storeLogo
    ) {
      formData.append('storeLogo', tierData.storeLogo);
    }
    if (tierData.tierIcon && tierData.tierIcon !== initialTierData?.tierIcon) {
      formData.append('tierIcon', tierData.tierIcon);
    }
    if (tierData.backgroundColor1 !== initialTierData?.backgroundColor1) {
      formData.append('backgroundColor1', tierData.backgroundColor1);
    }
    if (tierData.backgroundColor2 !== initialTierData?.backgroundColor2) {
      formData.append('backgroundColor2', tierData.backgroundColor2);
    }

    formData.append('benefitDescription', description);

    benefitData.forEach((benefit, index) => {
      const initialBenefit = initialBenefitData[index] || {};

      if (benefit.benefitId) {
        formData.append(`points[${index}].benefitId`, benefit.benefitId);
      }
      if (benefit.benefitTitle !== initialBenefit.benefitTitle) {
        formData.append(`points[${index}].benefitsText`, benefit.benefitTitle);
      }
      if (
        benefit.benefitIcon &&
        benefit.benefitIcon !== initialBenefit.benefitIcon
      ) {
        formData.append(`points[${index}].benefitsIcon`, benefit.benefitIcon);
      }
    });

    formData.forEach((value, key) => {
      console.log(`${key}:`, value);
    });

    try {
      const response = await apiRequest('updateTier', formData);
      if (response.error) {
        toast.error(
          response.error?.error || 'An error occurred while updating the tier.'
        );
      } else {
        navigator('/tier');
        toast.success('Tier updated successfully.');
      }
    } catch (error) {
      console.error('Error during update:', error);
      toast.error('Failed to update the tier. Please try again.');
    } finally {
      setLoader(false);
    }
  };

  const handleSubmit = async () => {
    if (isEdit) {
      handleUpdate();
    } else {
      handleCreate();
    }
  };

  const handleInputChange = (
    e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>
  ) => {
    const { name, value } = e.target;
    setTierData((prev: any) => ({ ...prev, [name]: value }));
  };

  const handleBenefitPointsChange = (
    idx: number,
    e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>
  ) => {
    const { name, value } = e.target;

    setBenefitData((prevBenefits) =>
      prevBenefits.map((benefit, index) =>
        index === idx ? { ...benefit, [name]: value } : benefit
      )
    );
  };

  const handleIconChange = (
    idx: number,
    e: React.ChangeEvent<HTMLInputElement>
  ) => {
    const file = e.target.files?.[0] || null;

    setBenefitData((benefit) =>
      benefit.map((benefitData, index) =>
        index === idx ? { ...benefitData, benefitIcon: file } : benefitData
      )
    );
  };

  const getTierDataById = async () => {
    if (isEdit && tierId) {
      setLoader(true);
      try {
        const res = await apiRequest(`tier/get/${tierId}`);
        if (res.error) {
          toast.error('Failed to get data.');
          navigator('/tier');
          return;
        }

        const data: TierResponseType = res.data;

        const updatedTierData: TierDataType = {
          id: data.tierId.toString(),
          rangeFrom: data.rangeFrom.toString(),
          rangeTo: data.rangeTo.toString(),
          tierName: data.tierName,
          storeLogo: data.storeLogo,
          tierIcon: data.tierIcon,
          backgroundColor: data.backgroundColor,
          programHeading: data.programHeading,
          programSubDescription: data.programSubDescription,
          milestoneHeading: data.milestoneHeading,
          milestoneDescription: data.milestoneDescription,
          milestoneProgress: data.milestoneProgress,
          milestoneContent: data.milestoneContent,
          isActive: data.status ? 'true' : 'false',
          ctaButtonName: data.ctaButtonName,
          ctaButtonLink: data.ctaButtonLink,
          ctaDescription: data.ctaDescription,
          title: data.benefitTitle,
          description: data.benefitDescription || '',
          backgroundColor1: data.benefitBackgroundColor1,
          backgroundColor2: data.benefitBackgroundColor2,
          benefitTitle: '',
        };
        setDescription(data.benefitDescription);

        const updatedBenefitData: BenefitType[] = data.benefits.map(
          (benefit, idx) => ({
            id: idx,
            benefitId: benefit.id.toString(),
            benefitIcon: benefit.benefitIcon || null,
            benefitTitle: benefit.title || '',
          })
        );

        console.log('updatedBenefitData', updatedBenefitData);

        setTierData(updatedTierData);
        setInitialTierData(updatedTierData);
        setBenefitData(updatedBenefitData);
        setInitialBenefitData(updatedBenefitData);
      } catch (error) {
        console.error('Error fetching tier data:', error);
        toast.error('Failed to get data. Please try again.');
        navigator('/tier');
      } finally {
        setLoader(false);
      }
    }
  };

  const openAreYouSurePopup = (idx: number) => {
    setIsPopupOpen(true);
    setPopupIndex(idx);
  };

  const confirmDeleteBenefit = () => {
    if (popupIndex !== null) {
      setBenefitData((prev) => prev.filter((_, idx) => idx !== popupIndex));
      setInitialBenefitData((prev) =>
        prev.filter((_, idx) => idx !== popupIndex)
      );
    }
    closeAreYouSurePopup();
  };

  const closeAreYouSurePopup = () => {
    setIsPopupOpen(false);
    setPopupIndex(null);
  };

  useEffect(() => {
    getTierDataById();
  }, [isEdit, tierId]);

  return (
    <>
      <Dialog onClose={closeAreYouSurePopup} open={isPopupOpen}>
        <DialogTitle>Are you sure?</DialogTitle>
        <List>
          <div className='p-4'>
            <p>
              Do you really want to delete this benefit point? This action
              cannot be undone.
            </p>
            <div className='flex justify-end gap-3 mt-4'>
              <button
                className='px-4 py-2 bg-gray-200 rounded-md text-black hover:bg-gray-300'
                onClick={closeAreYouSurePopup}
              >
                Cancel
              </button>
              <button
                className='px-4 py-2 bg-red-500 text-white rounded-md hover:bg-red-600'
                onClick={confirmDeleteBenefit}
              >
                Delete
              </button>
            </div>
          </div>
        </List>
      </Dialog>

      {loader && <Loader showHide={loader} />}
      {!loader && (
        <div className='px-5'>
          <CustomButton
            name={isEdit ? 'Update Tier' : 'Create Tier'}
            onClick={handleSubmit}
          />
          <Header title={tierData.tierName || 'Create New Tier'} />

          <div className='flex gap-5'>
            <div className='w-[60%]'>
              <BasicCard>
                <div className={headerStyle}>Spent in calendar year</div>
                <div className='flex gap-4 mt-2'>
                  <div className='w-[45%]'>
                    <label>Range From</label>
                    <input
                      type='number'
                      className={clsx(inputStyle, 'w-full')}
                      name='rangeFrom'
                      value={tierData.rangeFrom}
                      onChange={handleInputChange}
                    />
                  </div>
                  <div className='w-[45%]'>
                    <label>Range To</label>
                    <input
                      type='number'
                      className={clsx(inputStyle, 'w-full')}
                      name='rangeTo'
                      value={tierData.rangeTo}
                      onChange={handleInputChange}
                    />
                  </div>
                </div>
                <div className={noteStyle}>
                  Your first tier milestone must be zero in order to enroll
                  customers into your VIP flow. This tier cannot be deleted.
                </div>
              </BasicCard>

              <BasicCard>
                <div className={headerStyle}>General Settings</div>
                <div className='mt-3 flex justify-between gap-1'>
                  <div className='w-[50%]'>
                    <div className='font-light text-[14px]'>Store Logo</div>
                    <div
                      className='border h-[130px] flex justify-center items-center cursor-pointer text-[15px] font-light text-[#717171] rounded-[5px] mt-2
                '
                      onClick={() => handleDivClick(storeLogoInputRef)}
                    >
                      {tierData.storeLogo ? (
                        <img
                          src={
                            tierData.storeLogo instanceof File
                              ? URL.createObjectURL(tierData.storeLogo)
                              : tierData.storeLogo
                          }
                          alt='storeLogo'
                          className='mt-[10px] w-[100px]'
                        />
                      ) : (
                        'Upload store logo'
                      )}
                    </div>

                    <input
                      type='file'
                      accept='image/*'
                      ref={storeLogoInputRef}
                      style={{ display: 'none' }}
                      onChange={(e) => handleFileChange(e, 'storeLogo')}
                    />
                    <div className={noteStyle}>
                      Upload image 200px * 140px and max 2MB
                    </div>
                  </div>

                  <div className='w-[50%]'>
                    <div className='font-light text-[14px]'>Tier Icon</div>
                    <div
                      className='border h-[130px] flex justify-center items-center cursor-pointer text-[15px] font-light text-[#717171] rounded-[5px] mt-2'
                      onClick={() => handleDivClick(tierIconInputRef)}
                    >
                      {tierData.tierIcon ? (
                        <img
                          src={
                            tierData.tierIcon instanceof File
                              ? URL.createObjectURL(tierData.tierIcon)
                              : tierData.tierIcon
                          }
                          alt='tierIcon'
                          className='mt-[10px] w-[100px]'
                        />
                      ) : (
                        'Upload tier icon'
                      )}
                    </div>
                    <input
                      type='file'
                      accept='image/*'
                      ref={tierIconInputRef}
                      style={{ display: 'none' }}
                      onChange={(e) => handleFileChange(e, 'tierIcon')}
                    />
                    <div className={noteStyle}>
                      Upload image 200px * 140px and max 2MB
                    </div>
                  </div>
                </div>

                <InputCardItems>
                  <div className='w-[50%] '>
                    <div>Background Color</div>
                    <div className={clsx(inputStyle)}>
                      <input
                        type='color'
                        name='backgroundColor'
                        value={tierData.backgroundColor}
                        onChange={handleInputChange}
                      />
                      <span>{tierData.backgroundColor}</span>
                    </div>
                  </div>
                  <div className='w-[50%]'>
                    <div>Tier Name</div>
                    <input
                      placeholder='Enter Tier Name'
                      type='text'
                      value={tierData.tierName}
                      name='tierName'
                      onChange={handleInputChange}
                      className={clsx(inputStyle)}
                    />
                  </div>
                </InputCardItems>

                <InputCardItems>
                  <div className='w-[50%]'>
                    <div>Program Heading</div>
                    <input
                      placeholder='Enter Program Heading'
                      type='text'
                      className={clsx(inputStyle)}
                      onChange={handleInputChange}
                      name='programHeading'
                      value={tierData.programHeading}
                    />
                  </div>

                  <div className='w-[50%]'>
                    <div>Program Sub-Description</div>
                    <input
                      placeholder='Enter Program Sub-Description'
                      type='text'
                      className={clsx(inputStyle)}
                      onChange={handleInputChange}
                      name='programSubDescription'
                      value={tierData.programSubDescription}
                    />
                  </div>
                </InputCardItems>

                <InputCardItems>
                  <div className='w-[50%]'>
                    <div>Milestone Heading</div>
                    <input
                      placeholder='Enter Milestone Heading'
                      type='text'
                      className={clsx(inputStyle)}
                      onChange={handleInputChange}
                      name='milestoneHeading'
                      value={tierData.milestoneHeading}
                    />
                  </div>

                  <div className='w-[50%]'>
                    <div>Milestone Description</div>
                    <input
                      placeholder='Enter Milestone Description'
                      type='text'
                      className={clsx(inputStyle)}
                      onChange={handleInputChange}
                      name='milestoneDescription'
                      value={tierData.milestoneDescription}
                    />
                  </div>
                </InputCardItems>

                <InputCardItems>
                  <div className='w-[50%]'>
                    <div>Milestone Progress</div>
                    <input
                      placeholder='Enter Milestone Progress'
                      type='text'
                      className={clsx(inputStyle)}
                      onChange={handleInputChange}
                      name='milestoneProgress'
                      value={tierData.milestoneProgress}
                    />
                  </div>

                  <div className='w-[50%]'>
                    <div>Milestone Content</div>
                    <input
                      placeholder='Enter Milestone Content'
                      type='text'
                      className={clsx(inputStyle)}
                      onChange={handleInputChange}
                      name='milestoneContent'
                      value={tierData.milestoneContent}
                    />
                  </div>
                </InputCardItems>
              </BasicCard>

              <BasicCard>
                <div className={headerStyle}>Benefits</div>

                <InputCardItems>
                  <div className='w-[50%]'>
                    <div>Title</div>
                    <input
                      placeholder='Enter Title'
                      type='text'
                      className={clsx(inputStyle)}
                      onChange={handleInputChange}
                      value={tierData.title}
                      name='title'
                    />
                  </div>
                </InputCardItems>

                <InputCardItems>
                  <div className='w-[50%]'>
                    <div>Description</div>
                    <ReactQuill
                      value={description}
                      onChange={setDescription}
                      theme='snow'
                      placeholder='Start writing here...'
                      modules={modules}
                      style={{ height: '300px', marginBottom: '50px' }}
                    />
                  </div>
                </InputCardItems>

                <InputCardItems>
                  <div className='w-[50%] '>
                    <div>Background Color 1</div>
                    <div className={clsx(inputStyle)}>
                      <input
                        type='color'
                        onChange={handleInputChange}
                        value={tierData.backgroundColor1}
                        name='backgroundColor1'
                      />
                      <span>
                        {tierData.backgroundColor1 ||
                          'Select Background Color 1'}
                      </span>
                    </div>
                  </div>
                </InputCardItems>

                <InputCardItems>
                  <div className='w-[50%] '>
                    <div>Background Color 2</div>
                    <div className={clsx(inputStyle)}>
                      <input
                        type='color'
                        name='backgroundColor2'
                        onChange={handleInputChange}
                        value={tierData.backgroundColor2}
                      />
                      <span>
                        {tierData.backgroundColor2 ||
                          'Select Background Color 2'}
                      </span>
                    </div>
                  </div>
                </InputCardItems>

                {benefitData &&
                  benefitData.length > 0 &&
                  benefitData.map((benefit, idx) => {
                    return (
                      <div>
                        <InputCardItems>
                          <div className='w-[50%]'>
                            <div>Benefit Text {benefit.id + 1}</div>
                            <input
                              name='benefitTitle'
                              placeholder='Enter Benefit Text'
                              type='text'
                              className={clsx(inputStyle)}
                              value={benefit.benefitTitle}
                              onChange={(e) =>
                                handleBenefitPointsChange(idx, e)
                              }
                            />
                          </div>

                          {typeof benefit?.benefitIcon === 'string' && (
                            <div>
                              <img
                                src={benefit?.benefitIcon}
                                className='w-10 h-10 mx-2 mt-5'
                              />
                            </div>
                          )}

                          <div className='w-[50%]'>
                            <div>Upload the Icon</div>
                            <input
                              name='benefitIcon'
                              type='file'
                              className={clsx(inputStyle)}
                              onChange={(e) => handleIconChange(idx, e)}
                            />
                          </div>

                          <div className='mt-5 mx-2'>
                            <MdDelete
                              size={20}
                              className='text-red-500 cursor-pointer'
                              onClick={() => openAreYouSurePopup(idx)}
                            />
                          </div>
                        </InputCardItems>
                      </div>
                    );
                  })}

                <InputCardItems>
                  <div
                    className='w-[116px] h-[36px] flex items-center justify-center border border-black rounded-[9px] text-[12px] text-[#000000] font-normal cursor-pointer select-none'
                    onClick={addMorePoints}
                  >
                    Add More Point
                  </div>
                </InputCardItems>

                <div className='my-5'>
                  <CustomLine />
                </div>

                <InputCardItems>
                  <div className='w-[50%]'>
                    <div>CTA Button Name</div>
                    <input
                      placeholder='Enter CTA Button Name'
                      type='text'
                      className={clsx(inputStyle)}
                      name='ctaButtonName'
                      onChange={handleInputChange}
                      value={tierData.ctaButtonName}
                    />
                  </div>

                  <div className='w-[50%]'>
                    <div>CTA Button Link</div>
                    <input
                      placeholder='Enter CTA Button Link'
                      type='text'
                      className={clsx(inputStyle)}
                      name='ctaButtonLink'
                      onChange={handleInputChange}
                      value={tierData.ctaButtonLink}
                    />
                  </div>
                </InputCardItems>

                <InputCardItems>
                  <div className='w-[50%]'>
                    <div>CTA Description</div>
                    <input
                      placeholder='Enter CTA Description'
                      type='text'
                      className={clsx(inputStyle)}
                      name='ctaDescription'
                      onChange={handleInputChange}
                      value={tierData.ctaDescription}
                    />
                  </div>
                </InputCardItems>
              </BasicCard>
            </div>

            <div className='w-[40%]'>
              <BasicCard>
                <div>
                  <div className={headerStyle}>Summary</div>
                  <div className={noteStyle}>
                    Customers earn 1 point for every ₹1 spent.
                  </div>
                  <div className='w-full border my-5' />
                  <div>
                    <div className={headerStyle}>Status</div>
                    <div>
                      <div>
                        <input
                          type='radio'
                          name='isActive'
                          value='true'
                          checked={tierData.isActive === 'true'}
                          onChange={handleInputChange}
                        />
                        <label className={clsx('ml-2', noteStyle)}>
                          Active
                        </label>
                      </div>
                      <div>
                        <input
                          type='radio'
                          name='isActive'
                          value='false'
                          checked={tierData.isActive === 'false'}
                          onChange={handleInputChange}
                        />
                        <label className={clsx('ml-2', noteStyle)}>
                          Disabled
                        </label>
                      </div>
                    </div>
                  </div>
                </div>
              </BasicCard>
            </div>
          </div>
        </div>
      )}
    </>
  );
};

export default TierForm;
